import * as React from "react";

import { Box } from "../../components/ui/box";
import type { Omittable } from "../../lib/props";

/** Toolbar row height; single-consumer constant owned by this particle. */
const TOOLBAR_HEIGHT = 40;

// --- Slot names ---

const SLOT_NAMES = {
  left: "DataTableToolbarLeft",
  actions: "DataTableToolbarActions",
} as const;

type SlotKey = keyof typeof SLOT_NAMES;

type Slots = {
  left: React.ReactElement | null;
  actions: React.ReactElement | null;
};

const EMPTY_SLOTS: Slots = { left: null, actions: null };

// --- Slot collection (displayName-based) ---

type ChildWithChildren = React.ReactElement<{ children?: React.ReactNode }>;

function collectSlots(children: React.ReactNode): Slots {
  const slots: Slots = { ...EMPTY_SLOTS };

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const element = child as ChildWithChildren;
    if (element.type === React.Fragment) {
      const nested = collectSlots(element.props.children);
      (Object.keys(nested) as SlotKey[]).forEach((key) => {
        if (nested[key] && !slots[key]) slots[key] = nested[key];
      });
      return;
    }
    const displayName = (element.type as { displayName?: string })?.displayName;
    const slotKey = (Object.keys(SLOT_NAMES) as SlotKey[]).find(
      (key) => SLOT_NAMES[key] === displayName,
    );
    if (slotKey && !slots[slotKey]) {
      slots[slotKey] = element;
    }
  });

  return slots;
}

// --- DataTableToolbar ---

type DataTableToolbarProps = Omittable<{
  children: React.ReactNode;
  hidden?: boolean;
}>;

function DataTableToolbar({ children, hidden = false }: DataTableToolbarProps) {
  if (hidden) return null;

  const slots = collectSlots(children);
  const hasLeft = !!slots.left;
  const hasActions = !!slots.actions;
  const justify =
    hasLeft && hasActions ? "between" : hasActions ? "end" : "start";

  return (
    <Box paddingX="md" paddingY="sm" minHeight={TOOLBAR_HEIGHT}>
      <Box direction="row" align="center" justify={justify} gap="xs">
        {slots.left}
        {slots.actions}
      </Box>
    </Box>
  );
}

DataTableToolbar.displayName = "DataTableToolbar";

// --- Slot components ---

type SlotProps = { children?: React.ReactNode };

function DataTableToolbarLeft({ children }: SlotProps) {
  return <>{children}</>;
}
DataTableToolbarLeft.displayName = SLOT_NAMES.left;

function DataTableToolbarActions({ children }: SlotProps) {
  return (
    <Box direction="row" align="center" gap="xs">
      {children}
    </Box>
  );
}
DataTableToolbarActions.displayName = SLOT_NAMES.actions;

export { DataTableToolbar, DataTableToolbarLeft, DataTableToolbarActions };
